// src/hooks/useSearch.js
import { useState, useCallback, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const MODEL_KEY = "ai_default_model";
const MODEL_FALLBACK = "gemini-2.5-flash-lite";

function extractDomainFromUrl(url) {
  // English comment: Extract a normalized domain from a URL (best-effort).
  if (!url || typeof url !== "string") return "";
  try {
    const u = new URL(url);
    let host = (u.hostname || "").toLowerCase();
    if (host.startsWith("www.")) host = host.slice(4);
    return host;
  } catch {
    // English comment: Fallback for non-absolute URLs.
    const m = url.toLowerCase().match(/^https?:\/\/([^/]+)/);
    if (!m) return "";
    let host = m[1];
    if (host.startsWith("www.")) host = host.slice(4);
    return host;
  }
}

export default function useSearch() {
  const [searchTerm, setSearchTerm] = useState("");
  const [count, setCount] = useState(10);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  const [aiModel, setAiModel] = useState(MODEL_FALLBACK);

  // NEW: link-list action state
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState("");
  const [markSuccess, setMarkSuccess] = useState("");

  const eventSourceRef = useRef(null);

  // English comment: Keep a persistent set of domains across "load more" calls.
  const seenDomainsRef = useRef(new Set());

  const closeStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const fetchAiModel = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/settings/${MODEL_KEY}`, {
        withCredentials: true,
        timeout: 15000,
      });

      const found = res?.data?.found;
      const value = res?.data?.value;

      if (found === false) {
        setAiModel(MODEL_FALLBACK);
        return;
      }

      if (typeof value === "string" && value.trim()) {
        setAiModel(value.trim());
      } else {
        setAiModel(MODEL_FALLBACK);
      }
    } catch (e) {
      // English comment: Never block UI if settings endpoint fails.
      setAiModel(MODEL_FALLBACK);
    }
  }, []);

  const startStream = useCallback(
    ({ term, pageSize, startOffset, resetResults }) => {
      if (!term) {
        setError("Bitte geben Sie einen Suchbegriff ein.");
        setLoading(false);
        return;
      }

      if (resetResults) {
        setResults([]);
        setOffset(0);
        // English comment: Reset domain memory when starting a fresh search.
        seenDomainsRef.current = new Set();
      }

      setError("");
      setLoading(true);
      setHasMore(false);
      setHasSearched(true);

      // English comment: Send all already-seen domains to backend to dedupe across pages.
      const seenDomainsArr = Array.from(seenDomainsRef.current);
      const seenDomainsParam = encodeURIComponent(seenDomainsArr.join(","));

      const url =
        `${API_BASE}/search-stream` +
        `?search_term=${encodeURIComponent(term)}` +
        `&count=${pageSize}` +
        `&offset=${startOffset}` +
        `&seen_domains=${seenDomainsParam}`;

      const es = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = es;

      es.addEventListener("result", (event) => {
        try {
          const item = JSON.parse(event.data);

          // English comment: Add domain to local seen set as soon as we receive an item.
          const link = item?.link || item?.formattedUrl || "";
          const d = extractDomainFromUrl(link);
          if (d) seenDomainsRef.current.add(d);

          setResults((prev) => [...prev, item]);
        } catch (e) {
          console.error("Failed to parse result event", e);
        }
      });

      es.addEventListener("done", (event) => {
        try {
          const data = JSON.parse(event.data);
          const returned = data.returned_count || 0;

          setHasMore(returned > 0);

          if (typeof data.next_offset === "number") setOffset(data.next_offset);
          else setOffset((prev) => prev + returned);
        } catch (e) {
          console.error("Failed to parse done event", e);
        }

        setLoading(false);
        closeStream();
      });

      es.addEventListener("error", (event) => {
        console.error("SSE error", event);
        setError("Fehler während der Suche.");
        setLoading(false);
        setHasMore(false);
        closeStream();
      });
    },
    [closeStream]
  );

  const handleSearch = useCallback(() => {
    closeStream();

    startStream({
      term: searchTerm,
      pageSize: count,
      startOffset: 0,
      resetResults: true,
    });
  }, [closeStream, startStream, searchTerm, count]);

  const handleLoadMore = useCallback(() => {
    if (loading) return;

    closeStream();

    startStream({
      term: searchTerm,
      pageSize: count,
      startOffset: offset,
      resetResults: false,
    });
  }, [closeStream, startStream, searchTerm, count, offset, loading]);

  const handleExport = useCallback(() => {
    const columns = [
      "title",
      "street",
      "zipcode",
      "city",
      "country",
      "firstname",
      "lastname",
      "phonenumber",
      "email",
      "formattedUrl",
      "cms",
      "snippet",
    ];

    const filtered = results.map((row) => {
      const obj = {};
      columns.forEach((col) => {
        obj[col] = row[col] ?? "";
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    const safe = (searchTerm || "export")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^\w.-]/g, "_");

    XLSX.writeFile(wb, `${safe}.xlsx`);
  }, [results, searchTerm]);

  // NEW: Mark result URL as Link List (with tags)
  const markAsLinkList = useCallback(async (url, tags = []) => {
    setMarkError("");
    setMarkSuccess("");

    const u = (url || "").trim();
    if (!u) {
      setMarkError("Keine URL vorhanden.");
      return;
    }

    // TODO: Replace with auth context user id
    const createdBy = 1;

    setMarking(true);
    try {
      await axios.post(
        `${API_BASE}/api/link-lists`,
        {
          url: u,
          created_by: createdBy,
          is_active: true,
          tags: Array.isArray(tags) ? tags : [],
        },
        { withCredentials: true, timeout: 15000 }
      );

      setMarkSuccess("Als Link Liste gespeichert.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        setMarkError("URL ist bereits als Link Liste vorhanden.");
      } else {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Speichern fehlgeschlagen.";
        setMarkError(String(msg));
      }
    } finally {
      setMarking(false);
    }
  }, []);

  // Replace ONLY the blockDomain function with this version.
  const blockDomain = useCallback(async (domain, tags = [], isActive = true) => {
    setMarkError("");
    setMarkSuccess("");

    const d = (domain || "").trim().toLowerCase();
    if (!d) {
      setMarkError("Keine Domain vorhanden.");
      return;
    }

    setMarking(true);
    try {
      // 1) Create blocked domain (backend expects bulk payload: { domains: [...] })
      const createRes = await axios.post(
        `${API_BASE}/api/blocked-domains`,
        {
          domains: [d],
          is_active: !!isActive,
          // English comment: Optional AI fields could be added here if you want:
          // ai_rating: false, ai_reason: null, ai_probability: null,
        },
        { withCredentials: true, timeout: 15000 }
      );

      // 2) Resolve created domain id (best-effort)
      let createdId = null;

      // English comment: If backend returns created rows, try to pick the matching one.
      const cd = createRes?.data;
      if (Array.isArray(cd)) {
        const hit = cd.find((x) => (x?.domain || "").toLowerCase() === d);
        if (hit?.id != null) createdId = hit.id;
      } else if (cd && typeof cd === "object") {
        // e.g. {id, domain} or {created:[...]}
        if (cd?.id != null && (cd?.domain || "").toLowerCase() === d) createdId = cd.id;
        if (!createdId && Array.isArray(cd?.created)) {
          const hit = cd.created.find((x) => (x?.domain || "").toLowerCase() === d);
          if (hit?.id != null) createdId = hit.id;
        }
      }

      // English comment: Fallback - fetch list and find id by domain.
      if (!createdId) {
        const listRes = await axios.get(`${API_BASE}/api/blocked-domains`, {
          withCredentials: true,
          timeout: 15000,
        });
        const list = Array.isArray(listRes.data) ? listRes.data : [];
        const hit = list.find((x) => (x?.domain || "").toLowerCase() === d);
        if (hit?.id != null) createdId = hit.id;
      }

      // 3) Add tags via tags endpoint (if we have an id)
      const tagList = Array.isArray(tags)
        ? tags.map((t) => String(t).trim()).filter(Boolean)
        : [];
      if (createdId && tagList.length) {
        for (const tag of tagList) {
          await axios.post(
            `${API_BASE}/api/blocked-domains/${createdId}/tags`,
            { tag },
            { withCredentials: true, timeout: 15000 }
          );
        }
      }

      setMarkSuccess("Domain wurde blockiert.");
    } catch (e) {
      const status = e?.response?.status;

      if (status === 409) {
        setMarkError("Domain ist bereits blockiert.");
      } else {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Speichern fehlgeschlagen.";
        setMarkError(String(msg));
      }
    } finally {
      setMarking(false);
    }
  }, []);

  useEffect(() => {
    fetchAiModel();
    return () => {
      closeStream();
    };
  }, [fetchAiModel, closeStream]);

  return {
    searchTerm,
    setSearchTerm,
    count,
    setCount,
    results,
    error,
    loading,
    hasMore,
    hasSearched,
    aiModel,
    fetchAiModel,
    handleSearch,
    handleLoadMore,
    handleExport,

    blockDomain,
    markAsLinkList,
    marking,
    markError,
    markSuccess,
    setMarkError,
    setMarkSuccess,
  };
}